import axios from 'axios';

// Get API URL from environment variable or based on NODE_ENV
// Development: localhost:5001
// Production: ambulance.api.binimise.com
const getApiBaseUrl = (): string => {
  // If REACT_APP_API_URL is explicitly set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Otherwise, use environment-based defaults
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5001/api';
  }

  // Production default
  return 'https://ambulance.api.binimise.com/api';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 Unauthorized - authentication failed (token expired/invalid)
    if (error.response?.status === 401) {
      // Don't redirect on login endpoints - let them handle the error
      const isLoginEndpoint = error.config?.url?.includes('/auth/login') ||
        error.config?.url?.includes('/auth/system/login') ||
        error.config?.url?.includes('/auth/login/phone') ||
        error.config?.url?.includes('/auth/reset-password');

      // Don't redirect on /auth/me endpoint - let AuthContext handle it
      const isGetCurrentUser = error.config?.url?.includes('/auth/me');

      if (!isLoginEndpoint && !isGetCurrentUser) {
        // Only redirect if not on login page and not a login request
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/system' && currentPath !== '/reset-mpin' && currentPath !== '/change-password') {
          console.warn('[API Interceptor] 401 error, redirecting to login', {
            url: error.config?.url,
            currentPath
          });
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }
    // 403 Forbidden - user is authenticated but doesn't have permission
    // Don't log out on 403, just let the error propagate so the UI can handle it
    if (error.response?.status === 403) {
      console.warn('[API Interceptor] 403 Forbidden - insufficient permissions', {
        url: error.config?.url,
        message: error.response?.data?.message
      });
      // Don't redirect or log out - just let the component handle the error
    }
    return Promise.reject(error);
  }
);

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: {
      id?: string;
      name: string;
      email?: string;
      phone?: string;
      username?: string;
      role?: string;
      tenantId?: string;
      tenantName?: string;
      isSystem?: boolean;
      type: string;
      permissions?: Array<{ id: string; name: string }>;
    };
  };
}

export interface User {
  id?: string;
  _id?: string;
  name: string;
  email?: string;
  phone?: string;
  username?: string;
  roles?: string[]; // Array of roles
  role?: string; // Deprecated: kept for backward compatibility
  tenantId?: string;
  tenantName?: string;
  hospitalName?: string; // Hospital name from tenant
  logo?: string; // Hospital logo from tenant
  avatar?: string; // User avatar
  isSystem?: boolean;
  type: string;
  permissions?: Array<{ id: string; name: string }>;
  // For database user objects (from getUsers, etc.)
  metadata?: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec?: {
    tenantId?: string;
    email?: string;
    phone?: string;
    avatar?: string;
    roles: string[]; // Array of roles
    role?: string; // Deprecated: kept for backward compatibility
    customPermissions: Array<{ id: string; name: string }>;
    disabledPermissions: string[];
    staffCategory?: string;
    status: string;
    emailVerified: boolean;
    phoneVerified: boolean;
    personalInfo?: {
      // Legacy location for avatar (new field is spec.avatar)
      avatar?: string;
      dob?: number;
      gender?: string;
      guardianName?: string;
    };
    employment?: {
      supervisorId?: string;
      employeeId?: string;
      isSupervisor?: boolean;
      approval?: 'approved' | 'unapproved';
    };
    bankDetails?: {
      ifscCode?: string;
      bankName?: string;
      bankBranchName?: string;
      accountNumber?: string;
    };
    kyc?: {
      aadhaarNumber?: string;
      drivingLicenseNumber?: string;
      drivingLicenseValidity?: string; // Date string (e.g. YYYY-MM-DD)
    };
    financial?: {
      epfNumber?: string;
      uanNumber?: string;
      esicNumber?: string;
    };
    insurance?: {
      insuranceNumber?: string;
    };
    isAttachedToAmbulance?: boolean;
    ambulanceId?: string; // Reference to Ambulance _id
    ambulanceInfo?: {
      name?: string;
      licenceplate?: string;
      type?: string;
      deviceId?: string;
      mileage?: number;
      speedLimit?: number;
    };
    // Fields added by optimized tracking endpoints
    activeBooking?: {
      status: string;
      bookedBy?: string;
      bookedByDisplay?: string;
      pickupLocation?: {
        latitude: number;
        longitude: number;
      };
    } | null;
    location?: {
      latitude: number;
      longitude: number;
    };
    isOnDuty?: boolean;
  };
}

export interface Tenant {
  _id: string;
  metadata: {
    name: string;
    code: string; // Required code field
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    description?: string;
    domain?: string;
    email?: string;
    phone?: string;
    mPin?: string;
    address?: string;
    status: string;
    groupId?: string;
    hospital?: {
      name?: string;
      logo?: string;
      addressDetails?: string;
      addressFormatted?: string;
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      country?: string;
      phone?: string;
      email?: string;
      website?: string;
      registrationNumber?: string;
      licenseNumber?: string;
      emergencyContact?: string;
      emergencyPhone?: string;
      latitude?: number;
      longitude?: number;
      hospitalLocation?: {
        latitude: number;
        longitude: number;
      };
    };
    login_config?: {
      formDisplaySide?: 'left' | 'right';
      screenBackground?: string;
      pageLogo?: string;
      formLogo?: string;
      ownerImage?: string;
      ownerName?: string;
      ownerDesignation?: string;
      title?: string;
      subtitle?: string;
    };
    configSettings?: {
      vehicleMovementThresholdMeters?: number;
      strictDateAttendance?: boolean;
      haltReportMinHaltTimeSeconds?: number;
      haltReportMaxSpeedKmph?: number;
      haltReportMinDistanceMeters?: number;
      convertKnotsToKmph?: boolean;
      /** Polling interval for tracking dashboard: Current Route (in-trip) and Day Route (idle). Legacy key: currentRouteDataRefreshMs */
      dashboardRouteDataRefreshMs?: number;
      currentRouteDataRefreshMs?: number;
    };
  };
}

export interface StaffCategory {
  _id: string;
  metadata: {
    name: string;
    displayName?: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    description: string;
    tenantId?: string;
  };
}

export interface Patient {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
    tenantId?: string | null;
  };
  spec: {
    age: number;
    gender?: string;
    phone?: string;
    email?: string;
    address?: string;
    dateOfBirth?: number;
    bloodGroup?: string;
    emergencyContact?: {
      name?: string;
      phone?: string;
      relationship?: string;
    };
    medicalHistory?: string;
    allergies?: string;
    notes?: string;
    patientLocation?: {
      latitude: number;
      longitude: number;
    };
    vitalInfo?: Record<string, any>; // Dynamic vital information based on tenant's vital fields
  };
}

export interface Ambulance {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    licensePlate: string;
    vehicleNumber?: string;
    type?: string;
    status: string;
    capacity?: number;
    mileage?: number;
    speedLimit?: number;
    currentLocation?: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    assignedPilot?: {
      userId: string;
      name: string;
    };
    assignedStaffs?: Array<{
      userId: string;
      name: string;
    }>;
    gpsDevice?: {
      gpsDeviceId: string;
      deviceId: string;
    };
    notes?: string;
    tenantId?: string;
    image?: string;
  };
}

export interface AmbulanceType {
  _id: string;
  metadata: {
    name: string;
    displayName?: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    description: string;
    iconUrl?: string;
    tenantId?: string;
  };
}

export interface GpsDevice {
  _id: string;
  metadata: {
    name: string; // IMEI no. (required)
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    simNo?: string; // Legacy support
    msisdnNo?: string; // Legacy support
    traccarId?: number;
    simCards?: Array<{
      provider?: string; // Jio, BSNL, Airtel, VI
      simNo?: string;
      msisdnNo?: string;
    }>;
    description?: string;
    tenantId?: string;
    isAttached?: boolean;
    fuelDevice?: {
      enabled: boolean;
      subDeviceName?: string;
      typePorts?: Array<{
        type: string;
        port: string;
      }>;
      calibration?: Array<{
        kvats: number;
        litres: number;
      }>;
      threshold?: number;
    };
    ambulance?: {
      id: string;
      name: string;
      licensePlate: string;
      vehicleNumber?: string;
      mileage?: number;
      speedLimit?: number;
    };
  };
}

export interface UploadResponse {
  success: boolean;
  message: string;
  data: {
    url: string;
    fileName: string;
    filePath: string;
    originalName: string;
    size: number;
    contentType: string;
  };
}

// Auth APIs
export const systemLogin = (username: string, password: string) =>
  api.post<LoginResponse>('/auth/system/login', { username, password });

// Unified login - works for both tenant admins and regular users
// Get tenant info by code (public endpoint)
export const getTenantByCode = (code: string) =>
  api.get<{ success: boolean; message?: string; data: Tenant }>(`/get-tenant-info?code=${encodeURIComponent(code)}`);

// Get login config by tenant code (public endpoint). Returns { loginConfig, domain, tenantCode }.
export const getLoginConfigByTenantCode = (code: string) =>
  api.get<{ success: boolean; message?: string; data: { loginConfig: any; domain: string | null; tenantCode: string } }>(
    `/tenant/login-config?tenantCode=${encodeURIComponent(code)}`
  );

// Get tenant by domain (hostname) - public endpoint. Returns { tenantCode, domain, loginConfig }.
export const getTenantByDomain = (domain: string) =>
  api.get<{ success: boolean; message?: string; data: { tenantCode: string; domain: string | null; loginConfig: any } }>(
    `/tenant/by-domain?domain=${encodeURIComponent(domain)}`
  );

export const unifiedLogin = (email: string, password: string, tenantId: string) =>
  api.post<LoginResponse>('/auth/login', { email, password, tenantId });

// Unified phone login - works for both tenant admins and regular users
export const unifiedPhoneLogin = (phone: string, otp: string, tenantId: string) =>
  api.post<LoginResponse>('/auth/login/phone', { phone, otp, tenantId });

// Unified phone + MPIN login - no OTP. For users with mPin set.
export const unifiedPhoneMpinLogin = (phone: string, mPin: string, tenantId: string) =>
  api.post<LoginResponse>('/auth/login/phone/mpin', { phone, mPin, tenantId });

export const requestOTP = (phone: string, tenantCode: string) =>
  api.post('/users/auth/request-otp', { phone, tenantCode });

// Reset MPIN using OTP (no prior login required)
export const resetMpinWithOtp = (phone: string, tenantCode: string, otp: string, newMpin: string) =>
  api.post('/users/auth/reset-mpin', { phone, tenantCode, otp, newMpin });

export const resetPasswordWithCurrent = (
  email: string,
  tenantCode: string,
  currentPassword: string,
  newPassword: string
) =>
  api.post('/auth/reset-password', { email, tenantCode, currentPassword, newPassword });

export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post<{ success: boolean; message: string; data: { token: string } }>('/auth/change-password', {
    currentPassword,
    newPassword,
  });

// Get current user with latest permissions
export const getCurrentUser = () =>
  api.get<{ success: boolean; message: string; data: User }>('/auth/me');

// Tenant APIs
export const getTenants = (page: number = 1, pageSize: number = 10, search?: string) =>
  api.get<{ success: boolean; data: Tenant[]; pagination: any }>('/tenants', {
    params: { page, pageSize, ...(search && { search }) },
  });

export const getTenant = (id: string) =>
  api.get<{ success: boolean; data: Tenant }>(`/tenants/${id}`);

export const createTenant = (data: any) =>
  api.post<{ success: boolean; data: Tenant }>('/tenants', data);

export const updateTenant = (id: string, data: any) =>
  api.put<{ success: boolean; data: Tenant }>(`/tenants/${id}`, data);

export const deleteTenant = (id: string) =>
  api.delete<{ success: boolean; data: Tenant }>(`/tenants/${id}`);

/** Super Admin: tenant-level Admin login credentials (not Staff) */
export interface TenantAdminLogin {
  _id: string;
  metadata: {
    name?: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    tenantId: string;
    email: string;
    isEnabled: boolean;
  };
}

export const getTenantAdminLogins = (tenantId: string) =>
  api.get<{ success: boolean; data: TenantAdminLogin[] }>('/tenant-admin-logins', {
    params: { tenantId },
  });

export const createTenantAdminLogin = (data: {
  metadata?: { name?: string };
  spec: { tenantId: string; email: string; password: string; isEnabled?: boolean };
}) => api.post<{ success: boolean; data: TenantAdminLogin }>('/tenant-admin-logins', data);

export const updateTenantAdminLogin = (
  id: string,
  data: {
    metadata?: { name?: string };
    spec?: { email?: string; password?: string; isEnabled?: boolean };
  }
) => api.put<{ success: boolean; data: TenantAdminLogin }>(`/tenant-admin-logins/${id}`, data);

export const deleteTenantAdminLogin = (id: string) =>
  api.delete<{ success: boolean }>(`/tenant-admin-logins/${id}`);

// User APIs
export const getUsers = (
  page: number = 1,
  pageSize: number = 10,
  search?: string,
  status: 'active' | 'inactive' | 'all' = 'active'
) =>
  api.get<{ success: boolean; data: User[]; pagination: any }>('/users', {
    params: {
      page,
      pageSize,
      ...(search && { search }),
      ...(status && status !== 'active' ? { status } : { status: 'active' }),
    },
  });

export const getUserById = (id: string) =>
  api.get<{ success: boolean; data: User }>(`/users/${id}`);

export const createUser = (data: any) =>
  api.post<{ success: boolean; data: User }>('/users', data);

export const updateUser = (id: string, data: any) =>
  api.put<{ success: boolean; data: User }>(`/users/${id}`, data);

export const deleteUser = (id: string) =>
  api.delete<{ success: boolean; data: User }>(`/users/${id}`);

export const hardDeleteUser = (id: string) =>
  api.delete<{ success: boolean; data: User }>(`/users/${id}/hard`);

// Revoke user token - force user to logout
export const revokeUserToken = (id: string, reason?: string) =>
  api.post<{ success: boolean; message: string; data: { userId: string; revokedAt: string; revokedBy: string } }>(
    `/users/${id}/revoke-token`,
    { reason }
  );

// Staff Category APIs
export const getStaffCategories = (page: number = 1, pageSize: number = 10, search?: string) =>
  api.get<{ success: boolean; data: StaffCategory[]; pagination: any }>('/staff-categories', {
    params: { page, pageSize, ...(search && { search }) },
  });

export const getStaffCategory = (id: string) =>
  api.get<{ success: boolean; data: StaffCategory }>(`/staff-categories/${id}`);

export const createStaffCategory = (data: any) =>
  api.post<{ success: boolean; data: StaffCategory }>('/staff-categories', data);

export const updateStaffCategory = (id: string, data: any) =>
  api.put<{ success: boolean; data: StaffCategory }>(`/staff-categories/${id}`, data);

export const deleteStaffCategory = (id: string) =>
  api.delete<{ success: boolean; data: StaffCategory }>(`/staff-categories/${id}`);

// Cancel Reason APIs (predefined reasons for booking cancellation)
export interface CancelReason {
  _id: string;
  metadata: {
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    tenantId?: string | null;
    reason: string;
  };
}

export const getCancelReasons = (page: number = 1, pageSize: number = 10, search?: string) =>
  api.get<{ success: boolean; data: CancelReason[]; pagination: any }>('/cancel-reasons', {
    params: { page, pageSize, ...(search && { search }) },
  });

export const getCancelReasonsForDropdown = () =>
  api.get<{ success: boolean; data: CancelReason[] }>('/cancel-reasons/dropdown');

export const getCancelReason = (id: string) =>
  api.get<{ success: boolean; data: CancelReason }>(`/cancel-reasons/${id}`);

export const createCancelReason = (data: { spec: { reason: string } }) =>
  api.post<{ success: boolean; data: CancelReason }>('/cancel-reasons', data);

export const updateCancelReason = (id: string, data: { spec?: { reason?: string } }) =>
  api.put<{ success: boolean; data: CancelReason }>(`/cancel-reasons/${id}`, data);

export const deleteCancelReason = (id: string) =>
  api.delete<{ success: boolean; data: CancelReason }>(`/cancel-reasons/${id}`);

// Patient APIs
export const getPatients = (page: number = 1, pageSize: number = 10, search?: string) =>
  api.get<{ success: boolean; data: Patient[]; pagination: any }>('/patients', {
    params: { page, pageSize, ...(search && { search }) },
  });

// Role APIs
export const getRoles = (page: number = 1, pageSize: number = 10, search?: string) =>
  api.get<{ success: boolean; data: any[]; pagination: any }>('/roles', {
    params: { page, pageSize, ...(search && { search }) },
  });

export const getAvailableRoles = () => api.get<{ success: boolean; data: any[] }>('/roles/available');

export const getPatient = (id: string) =>
  api.get<{ success: boolean; data: Patient }>(`/patients/${id}`);

export const createPatient = (data: any) =>
  api.post<{ success: boolean; data: Patient }>('/patients', data);

export const updatePatient = (id: string, data: any) =>
  api.put<{ success: boolean; data: Patient }>(`/patients/${id}`, data);

export const deletePatient = (id: string) =>
  api.delete<{ success: boolean; data: Patient }>(`/patients/${id}`);

// Upload API
export const uploadFile = (formData: FormData) =>
  api.post<UploadResponse>('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Dashboard API
export interface DashboardData {
  stats: {
    totalAmbulances: number;
    availableAmbulances: number;
    onTripAmbulances: number;
    maintenanceAmbulances: number;
    offDutyAmbulances: number;
    staffOnDuty: number;
    totalStaff: number;
    availableStaff: number;
    tripsToday: number;
    livesSaved: number;
    livesSavedToday: number;
    allocatedTeams: number;
    readyToDispatch: number;
  };
  ambulanceTypeData: Record<string, { available: number; onDuty: number }>;
  staffByRole: Record<string, { available: number; onDuty: number; total: number }>;
  activeTrips: Array<{
    _id: string;
    ambulanceId: string;
    patientName: string;
    priority: string;
    crew: string;
    eta: string;
  }>;
  alerts: Array<{ type: 'warning' | 'error' | 'info'; message: string }>;
}

export const getDashboardData = () =>
  api.get<{ success: boolean; data: DashboardData }>('/dashboard');

// Ambulance Statistics API
export interface AmbulanceStats {
  total: number;
  byStatus: {
    available: number;
    onDuty: number;
    maintenance: number;
    offDuty: number;
  };
}

export const getAmbulanceStats = () =>
  api.get<{ success: boolean; data: AmbulanceStats }>('/dashboard/ambulances');

// Staff Statistics API
export interface StaffStats {
  total: number;
  byStatus: {
    active: number;
    inactive: number;
    suspended: number;
  };
  byDutyStatus: {
    onDuty: number;
    available: number;
  };
}

export const getStaffStats = () =>
  api.get<{ success: boolean; data: StaffStats }>('/dashboard/staff');

export interface MessageLog {
  _id: string;
  metadata: {
    name: string;
    tenantId?: string | null;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    channel: 'whatsapp';
    provider: 'interakt';
    templateName: string;
    language: string;
    receiverNumber: string;
    receiverName?: string;
    receiverId?: string;
    bookingId?: string;
    messageText?: string;
    params?: Record<string, string>;
    success: boolean;
    message: string;
    sentAt: number;
    errorMessage?: string;
  };
  receiverInfo?: {
    name?: string | null;
    phone?: string | null;
    patientName?: string | null;
    patientPhone?: string | null;
    patientLocation?: {
      latitude?: number | null;
      longitude?: number | null;
      address?: string | null;
    } | null;
  };
  bookingInfo?: {
    reference?: string | null;
    ambulanceInfo?: any;
    assignedStaffInfo?: any[];
    bookingAmount?: string | null;
    bookingType?: string | null;
    dropoffLocation?: any;
    pickupLocation?: any;
    status?: string | null;
    timings?: any;
    pilotHistory?: any[];
  };
  tenantInfo?: any;
}

export const getMessagesLogReport = (
  startDate?: number,
  endDate?: number,
  success?: 'true' | 'false' | '',
  channel?: 'whatsapp' | 'sms' | '',
  search?: string
) => {
  const params: Record<string, string | number> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (success === 'true' || success === 'false') params.success = success;
  if (channel === 'whatsapp' || channel === 'sms') params.channel = channel;
  if (search && search.trim()) params.search = search.trim();

  return api.get<{
    success: boolean;
    data: MessageLog[];
    message?: string;
    meta?: {
      total: number;
      returned: number;
      limit: number;
      truncated: boolean;
    };
  }>('/dashboard/messages-log-report', { params });
};

export const downloadMessagesLogReportPDF = (
  startDate?: number,
  endDate?: number,
  success?: 'true' | 'false' | '',
  channel?: 'whatsapp' | 'sms' | '',
  search?: string
) => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (success === 'true' || success === 'false') params.success = success;
  if (channel === 'whatsapp' || channel === 'sms') params.channel = channel;
  if (search && search.trim()) params.search = search.trim();
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>(
    '/dashboard/messages-log-report/download-pdf',
    { params }
  );
};

export const downloadMessagesLogReportExcel = (
  startDate?: number,
  endDate?: number,
  success?: 'true' | 'false' | '',
  channel?: 'whatsapp' | 'sms' | '',
  search?: string
) => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (success === 'true' || success === 'false') params.success = success;
  if (channel === 'whatsapp' || channel === 'sms') params.channel = channel;
  if (search && search.trim()) params.search = search.trim();
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>(
    '/dashboard/messages-log-report/download-excel',
    { params }
  );
};

// Ambulance APIs
export const getAmbulances = (page: number = 1, pageSize: number = 10, search?: string, status?: string) =>
  api.get<{ success: boolean; data: Ambulance[]; pagination: any }>('/ambulances', {
    params: { page, pageSize, ...(search && { search }), ...(status && { status }) },
  });

export const getAmbulance = (id: string) =>
  api.get<{ success: boolean; data: Ambulance }>(`/ambulances/${id}`);

export const createAmbulance = (data: any) =>
  api.post<{ success: boolean; data: Ambulance }>('/ambulances', data);

export const updateAmbulance = (id: string, data: any) =>
  api.put<{ success: boolean; data: Ambulance }>(`/ambulances/${id}`, data);

export const deleteAmbulance = (id: string) =>
  api.delete<{ success: boolean; data: Ambulance }>(`/ambulances/${id}`);

// Ambulance Assignment History APIs
export interface AmbulanceAssignmentHistory {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
    tenantId?: string | null;
  };
  spec: {
    ambulanceId: string;
    ambulanceInfo?: {
      name?: string;
      licensePlate?: string;
      vehicleNumber?: string;
      mileage?: number;
      speedLimit?: number;
    };
    action: 'assigned' | 'unassigned' | 'updated';
    assignedPilot?: {
      userId: string;
      name: string;
    } | null;
    assignedStaffs?: Array<{
      userId: string;
      name: string;
    }>;
    changedBy?: string;
    changedByInfo?: {
      name?: string;
      username?: string;
    };
    timestamp: number;
    notes?: string;
  };
}

export const getAmbulanceAssignmentHistory = (
  ambulanceId: string,
  page: number = 1,
  pageSize: number = 10,
  startDate?: string,
  endDate?: string,
  search?: string
) =>
  api.get<{ success: boolean; data: AmbulanceAssignmentHistory[]; pagination: any }>(
    `/ambulance-assignment-history/ambulance/${ambulanceId}`,
    {
      params: { page, pageSize, ...(startDate && { startDate }), ...(endDate && { endDate }), ...(search && { search }) },
    }
  );

export const getAllAssignmentHistory = (
  page: number = 1,
  pageSize: number = 10,
  ambulanceId?: string,
  pilotId?: string,
  staffId?: string,
  startDate?: string,
  endDate?: string,
  action?: string,
  search?: string
) =>
  api.get<{ success: boolean; data: AmbulanceAssignmentHistory[]; pagination: any }>(
    '/ambulance-assignment-history',
    {
      params: {
        page,
        pageSize,
        ...(ambulanceId && { ambulanceId }),
        ...(pilotId && { pilotId }),
        ...(staffId && { staffId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(action && { action }),
        ...(search && { search }),
      },
    }
  );

export const getPilotAssignmentHistory = (
  pilotId: string,
  page: number = 1,
  pageSize: number = 10,
  startDate?: string,
  endDate?: string
) =>
  api.get<{ success: boolean; data: AmbulanceAssignmentHistory[]; pagination: any }>(
    `/ambulance-assignment-history/pilot/${pilotId}`,
    {
      params: { page, pageSize, ...(startDate && { startDate }), ...(endDate && { endDate }) },
    }
  );

export const getStaffAssignmentHistory = (
  staffId: string,
  page: number = 1,
  pageSize: number = 10,
  startDate?: string,
  endDate?: string
) =>
  api.get<{ success: boolean; data: AmbulanceAssignmentHistory[]; pagination: any }>(
    `/ambulance-assignment-history/staff/${staffId}`,
    {
      params: { page, pageSize, ...(startDate && { startDate }), ...(endDate && { endDate }) },
    }
  );

export const downloadAssignmentHistoryPDF = (
  startDate?: string,
  endDate?: string,
  ambulanceId?: string,
  search?: string
) => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (ambulanceId) params.ambulanceId = ambulanceId;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>(
    '/ambulance-assignment-history/download-pdf',
    { params }
  );
};

export const downloadAssignmentHistoryExcel = (
  startDate?: string,
  endDate?: string,
  ambulanceId?: string,
  search?: string
) => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (ambulanceId) params.ambulanceId = ambulanceId;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>(
    '/ambulance-assignment-history/download-excel',
    { params }
  );
};

// Ambulance Type APIs
export const getAmbulanceTypes = (page: number = 1, pageSize: number = 100, search?: string) =>
  api.get<{ success: boolean; data: AmbulanceType[]; pagination: any }>('/ambulance-types', {
    params: { page, pageSize, ...(search && { search }) },
  });

export const getAmbulanceType = (id: string) =>
  api.get<{ success: boolean; data: AmbulanceType }>(`/ambulance-types/${id}`);

export const createAmbulanceType = (data: any) =>
  api.post<{ success: boolean; data: AmbulanceType }>('/ambulance-types', data);

export const updateAmbulanceType = (id: string, data: any) =>
  api.put<{ success: boolean; data: AmbulanceType }>(`/ambulance-types/${id}`, data);

export const deleteAmbulanceType = (id: string) =>
  api.delete<{ success: boolean; data: AmbulanceType }>(`/ambulance-types/${id}`);

// Tariff APIs
export interface Tariff {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
    tenantId?: string | null;
  };
  spec: {
    ambulanceTypeId: string;
    ambulanceTypeName?: string;
    rangeType: 'km_range' | 'per_km' | 'detention';
    minKm?: number;
    maxKm?: number;
    charge: number;
    description?: string;
  };
}

export const getTariffs = (page: number = 1, pageSize: number = 100, ambulanceTypeId?: string, search?: string) =>
  api.get<{ success: boolean; data: Tariff[]; pagination: any }>('/tariffs', {
    params: { page, pageSize, ...(ambulanceTypeId && { ambulanceTypeId }), ...(search && { search }) },
  });

export const getTariff = (id: string) =>
  api.get<{ success: boolean; data: Tariff }>(`/tariffs/${id}`);

export const createTariff = (data: any) =>
  api.post<{ success: boolean; data: Tariff }>('/tariffs', data);

export const updateTariff = (id: string, data: any) =>
  api.put<{ success: boolean; data: Tariff }>(`/tariffs/${id}`, data);

export const deleteTariff = (id: string) =>
  api.delete<{ success: boolean; data: Tariff }>(`/tariffs/${id}`);

export const calculateBookingAmount = (ambulanceTypeId: string, distanceKm: number) =>
  api.get<{ success: boolean; data: { amount: string | null; distanceKm: number; ambulanceTypeId: string } }>('/tariffs/calculate', {
    params: { ambulanceTypeId, distanceKm },
  });

// GPS Device APIs
export const getGpsDevices = (page: number = 1, pageSize: number = 100, onlyAvailable?: boolean, search?: string) =>
  api.get<{ success: boolean; data: GpsDevice[]; pagination: any }>('/gps-devices', {
    params: { page, pageSize, ...(onlyAvailable !== undefined && { onlyAvailable }), ...(search && { search }) },
  });

export const getGpsDevice = (id: string) =>
  api.get<{ success: boolean; data: GpsDevice }>(`/gps-devices/${id}`);

export const createGpsDevice = (data: any) =>
  api.post<{ success: boolean; data: GpsDevice }>('/gps-devices', data);

export const updateGpsDevice = (id: string, data: any) =>
  api.put<{ success: boolean; data: GpsDevice }>(`/gps-devices/${id}`, data);

export const deleteGpsDevice = (id: string) =>
  api.delete<{ success: boolean; data: GpsDevice }>(`/gps-devices/${id}`);

// Booking APIs
export interface Booking {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
    tenantId?: string | null;
  };
  spec: {
    patientId: string;
    patientInfo?: {
      name?: string;
      number?: string;
      contactPersonName?: string;
      contactPersonNumber?: string;
      dateOfBirth?: number;
      age?: number;
      gender?: string;
    };
    hospitalInfo?: {
      name?: string;
      number?: string;
    };
    ambulanceId?: string;
    ambulanceInfo?: {
      name?: string;
      type?: string; // Ambulance type displayName
      licensePlate?: string;
      deviceId?: string;
      mileage?: number;
      speedLimit?: number;
    };
    bookedBy: string;
    bookedByDisplay?: string;
    pilotId?: string;
    pilotInfo?: {
      name?: string;
      number?: string;
    };
    pilotHistory?: Array<{
      id: string;
      name?: string; // Populated dynamically from User, not stored
      phone?: string; // Populated dynamically from User, not stored
      status: 'assigned' | 'in_transit' | 'patient_picked' | 'patient_dropped' | 'completed' | 'cancelled';
      assignedTime?: number;
      inTransitTime?: number;
      patientPickedTime?: number;
      arrivedTime?: number; // legacy: use patientPickedTime ?? arrivedTime for display
      patientDroppedTime?: number;
      completionTime?: number;
      cancelledTime?: number;
      cancellationReason?: string; // Reason when pilot cancelled assignment
    }>;
    assignedStaffIds?: string[];
    assignedStaffInfo?: Array<{
      name?: string;
      number?: string;
    }>;
        timings?: {
          bookingTime: number;
          scheduledPickupTime?: number;
          estimatedArrivalTime?: number;
    actualArrivalTime?: number;
    completionTime?: number;
      pendingTime?: number;
      assignedTime?: number;
      inTransitTime?: number;
      patientPickedTime?: number;
      arrivedTime?: number; // legacy: use patientPickedTime ?? arrivedTime for display
      patientDroppedTime?: number;
      cancelledTime?: number;
    };
    pickupLocation: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    dropoffLocation: {
      latitude: number;
      longitude: number;
      address?: string;
    };
    status: 'pending' | 'assigned' | 'in_transit' | 'patient_picked' | 'patient_dropped' | 'completed' | 'cancelled';
    priority: 'low' | 'medium' | 'high' | 'critical';
    bookingType: 'to_hospital' | 'from_hospital';
    medicalCondition?: string;
    notes?: string;
    cancellationReason?: string;
    bookingAmount?: string;
  };
}

export const getBookings = (page: number = 1, pageSize: number = 10, status?: string, patientId?: string, ambulanceId?: string, priority?: string, search?: string, startDate?: string, endDate?: string, bookingType?: string) =>
  api.get<{ success: boolean; data: Booking[]; pagination: any }>('/bookings', {
    params: { page, pageSize, ...(status && { status }), ...(patientId && { patientId }), ...(ambulanceId && { ambulanceId }), ...(priority && { priority }), ...(search && { search }), ...(startDate && { startDate }), ...(endDate && { endDate }), ...(bookingType && { bookingType }) },
  });

export const getBooking = (id: string) =>
  api.get<{ success: boolean; data: Booking }>(`/bookings/${id}`);

export const createBooking = (data: any) =>
  api.post<{ success: boolean; data: Booking }>('/bookings', data);

export const updateBooking = (id: string, data: any) => {
  return api.put<{ success: boolean; data: Booking }>(`/bookings/${id}`, data)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.error('[Frontend API] Booking update failed', {
        bookingId: id,
        error: error.response?.data || error.message,
        status: error.response?.status,
        updateData: data,
      });
      throw error;
    });
};

export const deleteBooking = (id: string) =>
  api.delete<{ success: boolean; data: Booking }>(`/bookings/${id}`);

export const cancelPilotAssignment = (id: string, cancellationReason?: string) =>
  api.post<{ success: boolean; data: Booking; message?: string }>(`/bookings/${id}/cancel-pilot-assignment`, {
    cancellationReason,
  });

/** Reset an active trip to pending; frees ambulance; notifies tenant, pilot, creator, staff */
export const revertTripToPending = (bookingId: string, ambulanceId?: string) =>
  api.post<{ success: boolean; data: Booking; message?: string }>(`/bookings/${bookingId}/revert-trip-to-pending`, {
    ...(ambulanceId ? { ambulanceId } : {}),
  });

// Tracking APIs
export interface OnTripAmbulance {
  id: string;
  type: string;
  status: string;
  licensePlate: string;
  ambulanceId: string;
  mileage?: number;
  speedLimit?: number;
  currentLocation?: {
    latitude: number;
    longitude: number;
    address?: string;
  } | null;
}

export interface OnDutyStaff {
  name: string;
  role: string;
  userId: string;
  roles: string[];
}

export interface TrackingData {
  onTripAmbulances: OnTripAmbulance[];
  onDutyStaff: OnDutyStaff[];
}

export const getOnTripAmbulances = () =>
  api.get<{ success: boolean; data: OnTripAmbulance[] }>('/tracking/on-trip-ambulances');

export const getOnDutyStaff = () =>
  api.get<{ success: boolean; data: OnDutyStaff[] }>('/tracking/on-duty-staff');

export const getTrackingData = () =>
  api.get<{ success: boolean; data: TrackingData }>('/tracking/data');

export interface DailyMetrics {
  km: number;
  trips: number;
  timeTravelledHrs: number;
  engineHours: number;
  fuelRefill: number;
  fuelConsumed: number;
}

export interface AmbulanceRunningReportData {
  ambulanceId: string;
  ambulanceName: string;
  licensePlate: string | null;
  type: string | null;
  gpsDevice: string;
  totalKm: number;
  totalTrips: number;
  totalTimeTravelledHrs: number;
  totalEngineHours: number;
  totalFuelRefill: number;
  totalFuelConsumed: number;
  dailyData?: Record<string, DailyMetrics>;
  // Legacy field for backward compat
  dailyKm?: Record<string, number>;
}

export const getAmbulanceRunningReport = (date?: string, startDate?: string, endDate?: string) => {
  const params: any = {};
  if (startDate && endDate) {
    params.startDate = startDate;
    params.endDate = endDate;
  } else if (date) {
    params.date = date;
  }
  return api.get<{ success: boolean; data: AmbulanceRunningReportData[] }>('/tracking/ambulance-running-report', { params });
};

export const downloadAmbulanceRunningReportPDF = (date?: string, startDate?: string, endDate?: string) => {
  const params: any = {};
  if (startDate && endDate) {
    params.startDate = startDate;
    params.endDate = endDate;
  } else if (date) {
    params.date = date;
  }
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/tracking/ambulance-running-report/download-pdf', { params });
};

export const downloadAmbulanceRunningReportExcel = (date?: string, startDate?: string, endDate?: string) => {
  const params: any = {};
  if (startDate && endDate) {
    params.startDate = startDate;
    params.endDate = endDate;
  } else if (date) {
    params.date = date;
  }
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/tracking/ambulance-running-report/download-excel', { params });
};

// Halt Report API
export interface HaltDetail {
  startTime: string;
  endTime: string;
  duration: number;
  durationFormatted: string;
  timeRange: string;
  location: { lat: number; lng: number };
  address: string;
}

export interface HaltReportVehicle {
  slNo: number;
  vehicleNo: string;
  licensePlate: string | null;
  deviceImei: string | null;
  totalHalts: number;
  totalHaltDuration: number;
  totalHaltDurationFormatted: string;
  haltDetails: HaltDetail[];
}

export interface HaltReportData {
  date: string;
  configuration: {
    minHaltTimeSeconds: number;
    maxSpeedKmph: number;
    minDistanceMeters: number;
  };
  totalHalts: number;
  reports: HaltReportVehicle[];
}

export const getVehicleHaltReport = (
  date?: string,
  minHaltTimeSeconds?: number,
  maxSpeedKmph?: number,
  minDistanceMeters?: number
) => {
  const params: any = {};
  if (date) params.date = date;
  if (minHaltTimeSeconds !== undefined) params.minHaltTimeSeconds = minHaltTimeSeconds;
  if (maxSpeedKmph !== undefined) params.maxSpeedKmph = maxSpeedKmph;
  if (minDistanceMeters !== undefined) params.minDistanceMeters = minDistanceMeters;
  return api.get<{ success: boolean; data: HaltReportData }>('/tracking/halt-report', { params });
};

export const downloadVehicleHaltReportPDF = (
  date?: string,
  minHaltTimeSeconds?: number,
  maxSpeedKmph?: number,
  minDistanceMeters?: number
) => {
  const params: any = {};
  if (date) params.date = date;
  if (minHaltTimeSeconds !== undefined) params.minHaltTimeSeconds = minHaltTimeSeconds;
  if (maxSpeedKmph !== undefined) params.maxSpeedKmph = maxSpeedKmph;
  if (minDistanceMeters !== undefined) params.minDistanceMeters = minDistanceMeters;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/tracking/halt-report/download-pdf', { params });
};

export const downloadVehicleHaltReportExcel = (
  date?: string,
  minHaltTimeSeconds?: number,
  maxSpeedKmph?: number,
  minDistanceMeters?: number
) => {
  const params: any = {};
  if (date) params.date = date;
  if (minHaltTimeSeconds !== undefined) params.minHaltTimeSeconds = minHaltTimeSeconds;
  if (maxSpeedKmph !== undefined) params.maxSpeedKmph = maxSpeedKmph;
  if (minDistanceMeters !== undefined) params.minDistanceMeters = minDistanceMeters;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/tracking/halt-report/download-excel', { params });
};

export const downloadAttendanceReportPDF = (startDate: number, endDate: number) => {
  const params = { startDate, endDate };
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/attendance/report/download-pdf', { params });
};

export const downloadAttendanceReportExcel = (startDate: number, endDate: number) => {
  const params = { startDate, endDate };
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/attendance/report/download-excel', { params });
};

// Trips Report API
export interface TripsReportData {
  _id: string;
  reference: string;
  priority: string;
  bookingType: string;
  status: string;
  ambulance: string;
  pilot: string;
  bookingTime: number;
  lastUpdate: number;
  distanceCovered: number;
  fuelConsumption: number;
}

export const getTripsReport = (
  page: number = 1,
  pageSize: number = 1000,
  startDate?: number,
  endDate?: number,
  status?: string,
  priority?: string,
  bookingType?: string,
  ambulanceId?: string,
  search?: string
) => {
  const params: any = { page, pageSize };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (status && status !== 'all') params.status = status;
  if (priority && priority !== 'all') params.priority = priority;
  if (bookingType && bookingType !== 'all') params.bookingType = bookingType;
  if (ambulanceId && ambulanceId !== 'all') params.ambulanceId = ambulanceId;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: TripsReportData[]; pagination: any }>('/bookings/trips-report', { params });
};

export const downloadTripsReportPDF = (
  startDate: number,
  endDate: number,
  status?: string,
  priority?: string,
  bookingType?: string,
  ambulanceId?: string,
  search?: string
) => {
  const params: any = { startDate, endDate };
  if (status && status !== 'all') params.status = status;
  if (priority && priority !== 'all') params.priority = priority;
  if (bookingType && bookingType !== 'all') params.bookingType = bookingType;
  if (ambulanceId && ambulanceId !== 'all') params.ambulanceId = ambulanceId;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/bookings/trips-report/download-pdf', { params });
};

export const downloadTripsReportExcel = (
  startDate: number,
  endDate: number,
  status?: string,
  priority?: string,
  bookingType?: string,
  ambulanceId?: string,
  search?: string
) => {
  const params: any = { startDate, endDate };
  if (status && status !== 'all') params.status = status;
  if (priority && priority !== 'all') params.priority = priority;
  if (bookingType && bookingType !== 'all') params.bookingType = bookingType;
  if (ambulanceId && ambulanceId !== 'all') params.ambulanceId = ambulanceId;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/bookings/trips-report/download-excel', { params });
};

export const downloadBookingReportPDF = (
  startDate: number,
  endDate: number,
  status?: string,
  priority?: string,
  bookingType?: string,
  search?: string
) => {
  const params: any = { startDate, endDate };
  if (status) params.status = status;
  if (priority) params.priority = priority;
  if (bookingType) params.bookingType = bookingType;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/bookings/report/download-pdf', { params });
};

export const downloadBookingReportExcel = (
  startDate: number,
  endDate: number,
  status?: string,
  priority?: string,
  bookingType?: string,
  search?: string
) => {
  const params: any = { startDate, endDate };
  if (status) params.status = status;
  if (priority) params.priority = priority;
  if (bookingType) params.bookingType = bookingType;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/bookings/report/download-excel', { params });
};

export const downloadBookingDetailsPDF = (bookingId: string) => {
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>(
    `/bookings/${bookingId}/report/download-pdf`
  );
};

export const downloadBookingDetailsExcel = (bookingId: string) => {
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>(
    `/bookings/${bookingId}/report/download-excel`
  );
};

/**
 * Get ambulances with booking status (optimized endpoint)
 * Supports server-side filtering by status and running status
 */
export const getAmbulancesWithBookingStatus = (
  page: number = 1,
  pageSize: number = 100,
  search?: string,
  status?: string,
  runningStatus?: string
) =>
  api.get<{ success: boolean; data: Ambulance[]; pagination: any }>('/tracking/ambulances-with-status', {
    params: {
      page,
      pageSize,
      ...(search && { search }),
      ...(status && { status }),
      ...(runningStatus && { runningStatus }),
    },
  });

/**
 * Get staff with booking status (optimized endpoint)
 * Supports server-side filtering by status and duty status
 */
export const getStaffWithBookingStatus = (
  page: number = 1,
  pageSize: number = 100,
  search?: string,
  status?: string,
  dutyStatus?: string
) =>
  api.get<{ success: boolean; data: User[]; pagination: any }>('/tracking/staff-with-status', {
    params: {
      page,
      pageSize,
      ...(search && { search }),
      ...(status && { status }),
      ...(dutyStatus && { dutyStatus }),
    },
  });

/**
 * Get vehicle route by IMEI from external API
 * Calls the external route API directly: https://prod.api.binimise.com/v1/vehicle-route-by-imei
 */
export const getVehicleRouteByImei = async (ambulanceId: string, date: string, imei?: string) => {
  if (!imei) {
    throw new Error('IMEI is required to fetch vehicle route');
  }

  const ROUTE_API_BASE_URL = process.env.REACT_APP_ROUTE_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'https://dev.api.binimise.com/v1' : 'https://prod.api.binimise.com/v1');
  const ROUTE_API_SECRET_KEY = process.env.REACT_APP_ROUTE_API_SECRET_KEY || 'xX9dL7wQ8mT1eH4kS5yA0zUoIhBnCvR2pJ6tFgMbNqWsZdXcV3rKyLfGhT0uPwD';

  const url = `${ROUTE_API_BASE_URL}/vehicle-route-by-imei`;
  const params = {
    secretKey: ROUTE_API_SECRET_KEY,
    date: date,
    imei: imei,
  };

  // Use axios directly (not the api instance) to call external API without auth headers
  return axios.get(url, {
    params,
    timeout: 30000, // 30 seconds timeout
  });
};

/**
 * Get live location of ambulances/vehicles by groupId from external API
 * Calls the external API: https://prod.api.binimise.com/v1/ambulance-live-location
 */
export const getAmbulanceLiveLocation = async (groupId: string) => {
  if (!groupId) {
    throw new Error('groupId is required to fetch live locations');
  }

  const LIVE_LOCATION_API_BASE_URL = process.env.REACT_APP_ROUTE_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'https://dev.api.binimise.com/v1' : 'https://prod.api.binimise.com/v1');
  const LIVE_LOCATION_API_SECRET_KEY = process.env.REACT_APP_ROUTE_API_SECRET_KEY || 'xX9dL7wQ8mT1eH4kS5yA0zUoIhBnCvR2pJ6tFgMbNqWsZdXcV3rKyLfGhT0uPwD';

  const url = `${LIVE_LOCATION_API_BASE_URL}/ambulance-live-location`;
  const params = {
    groupId: groupId,
    secretKey: LIVE_LOCATION_API_SECRET_KEY,
  };

  // Use axios directly (not the api instance) to call external API without auth headers
  return axios.get(url, {
    params,
    timeout: 30000, // 30 seconds timeout
  });
};

/**
 * Get ambulance live location by deviceId (IMEI) from external API
 * Calls the external API: https://prod.api.binimise.com/v1/ambulance-live-location
 */
/**
 * Current Route live GPS via backend → Traccar (devices + positions).
 * GET /api/tracking/traccar-live-location?uniqueId={imei}
 */
export const getTraccarLiveLocation = (uniqueId: string) =>
  api.get('/tracking/traccar-live-location', { params: { uniqueId } });

/**
 * Day Route trail via backend → Traccar reports/route.
 * GET /api/tracking/traccar-day-route?uniqueId={imei}&date=YYYY-MM-DD
 */
export const getTraccarDayRoute = (uniqueId: string, date: string) =>
  api.get('/tracking/traccar-day-route', { params: { uniqueId, date } });

export interface TraccarDevice {
  id: number;
  uniqueId: string;
  name?: string;
  status?: string;
  lastUpdate?: string;
  positionId?: number;
  groupId?: number;
  attributes?: Record<string, unknown>;
  calendarId?: number;
  phone?: string | null;
  model?: string | null;
  contact?: string | null;
  category?: string | null;
  disabled?: boolean;
  expirationTime?: string | null;
}

/**
 * Traccar devices for a tenant group (filtered server-side by groupId).
 * GET /api/tracking/traccar-devices?groupId={groupId}
 */
export const getTraccarDevices = (groupId: number | string) =>
  api.get<{ success: boolean; data: TraccarDevice[]; message?: string }>('/tracking/traccar-devices', {
    params: { groupId },
  });

export const getAmbulanceLiveLocationByDeviceId = async (deviceId: string) => {
  if (!deviceId) {
    throw new Error('deviceId is required to fetch live location');
  }

  const LIVE_LOCATION_API_BASE_URL = process.env.REACT_APP_ROUTE_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'https://dev.api.binimise.com/v1' : 'https://prod.api.binimise.com/v1');
  const LIVE_LOCATION_API_SECRET_KEY = process.env.REACT_APP_ROUTE_API_SECRET_KEY || 'xX9dL7wQ8mT1eH4kS5yA0zUoIhBnCvR2pJ6tFgMbNqWsZdXcV3rKyLfGhT0uPwD';

  const url = `${LIVE_LOCATION_API_BASE_URL}/ambulance-live-location`;
  const params = {
    imeiNo: deviceId,
    secretKey: LIVE_LOCATION_API_SECRET_KEY,
  };

  // Use axios directly (not the api instance) to call external API without auth headers
  return axios.get(url, {
    params,
    timeout: 30000, // 30 seconds timeout
  });
};

/**
 * Get ambulance location history by IMEI and date from external API
 * Calls the external API: https://prod.api.binimise.com/v1/ambulance-location-history
 */
export const getAmbulanceLocationHistory = async (imei: string, date: string) => {
  if (!imei) {
    throw new Error('IMEI is required to fetch location history');
  }
  if (!date) {
    throw new Error('Date is required to fetch location history');
  }

  const LOCATION_HISTORY_API_BASE_URL = process.env.REACT_APP_ROUTE_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'https://dev.api.binimise.com/v1' : 'https://prod.api.binimise.com/v1');
  const LOCATION_HISTORY_API_SECRET_KEY = process.env.REACT_APP_ROUTE_API_SECRET_KEY || 'xX9dL7wQ8mT1eH4kS5yA0zUoIhBnCvR2pJ6tFgMbNqWsZdXcV3rKyLfGhT0uPwD';

  const url = `${LOCATION_HISTORY_API_BASE_URL}/ambulance-location-history`;
  const params = {
    imeiNo: imei,
    date: date,
    secretKey: LOCATION_HISTORY_API_SECRET_KEY,
  };

  // Use axios directly (not the api instance) to call external API without auth headers
  return axios.get(url, {
    params,
    timeout: 30000, // 30 seconds timeout
  });
};

/**
 * Get fuel data by IMEI and date range from external API
 * Calls the external API: https://dev.api.binimise.com/v1/fuel-rod-data
 */
export const getFuelData = async (imei: string, startDate: string, endDate: string) => {
  if (!imei) {
    throw new Error('IMEI is required to fetch fuel data');
  }
  if (!startDate) {
    throw new Error('startDate is required');
  }
  if (!endDate) {
    throw new Error('endDate is required');
  }

  const FUEL_API_BASE_URL = process.env.REACT_APP_ROUTE_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'https://dev.api.binimise.com/v1' : 'https://prod.api.binimise.com/v1');

  const url = `${FUEL_API_BASE_URL}/fuel-rod-data`;
  
  // Build query string manually to ensure proper encoding of nested parameters
  const queryParams = new URLSearchParams();
  queryParams.append('deviceInfo.imei', imei);
  queryParams.append('date[$gte]', startDate);
  queryParams.append('date[$lt]', endDate);
  
  const fullUrl = `${url}?${queryParams.toString()}`;

  // Use axios directly (not the api instance) to call external API without auth headers
  return axios.get(fullUrl, {
    timeout: 30000, // 30 seconds timeout
  });
};

/**
 * Get fuel rod data for all tenant ambulances in a date range.
 * GET /api/fuel/rod-data-all?date=YYYY-MM-DD
 * GET /api/fuel/rod-data-all?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
 */
export const getAllFuelRodData = (dateOrStartDate: string, endDate?: string) => {
  const params: Record<string, string> = {};
  if (endDate) {
    params.startDate = dateOrStartDate;
    params.endDate = endDate;
  } else {
    params.date = dateOrStartDate;
  }
  return api.get<{
    success: boolean;
    data: {
      startDate: string;
      endDate: string;
      count: number;
      items: Array<{
        ambulanceId: string;
        ambulanceName: string;
        licensePlate: string | null;
        imei: string;
        fuelRodData: any[];
        fuelReadings: Array<{ fuelLevel: number; recordedAt: string }>;
        fuelTimeline: any[];
        error?: string;
      }>;
    };
  }>('/fuel/rod-data-all', { params });
};

/**
 * Get tracking summary data (live location, location history, and fuel data)
 * Calls the internal API: /api/tracking/summary
 */
export const getTrackingSummary = async (imeiNo: string, secretKey?: string, startDate?: string, endDate?: string) => {
  if (!imeiNo) {
    throw new Error('IMEI is required to fetch tracking summary');
  }

  const ROUTE_API_SECRET_KEY = secretKey || process.env.REACT_APP_ROUTE_API_SECRET_KEY || 'xX9dL7wQ8mT1eH4kS5yA0zUoIhBnCvR2pJ6tFgMbNqWsZdXcV3rKyLfGhT0uPwD';

  const params: any = {
    imeiNo,
    secretKey: ROUTE_API_SECRET_KEY,
  };

  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }

  return api.get('/tracking/summary', { params });
};

// Attendance APIs
export interface Attendance {
  _id: string;
  metadata: {
    name: string;
    tenantId?: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    userId: string;
    status: 'checkedIn' | 'checkedOut';
    checkIn: {
      time: number;
      location: {
      latitude: number;
      longitude: number;
      address?: string;
    };
      image?: string;
    };
    checkOut?: {
      time: number;
      location: {
      latitude: number;
      longitude: number;
      address?: string;
    };
      image?: string;
    };
  };
}

export const checkIn = (
  userId: string,
  latitude: number,
  longitude: number,
  checkInImage?: string,
  checkInTime?: number
) =>
  api.post<{ success: boolean; data: Attendance }>('/attendance/check-in', {
    userId,
    latitude,
    longitude,
    ...(checkInImage && { checkInImage }),
    ...(checkInTime && { checkInTime }),
  });

export const checkOut = (
  userId: string,
  latitude: number,
  longitude: number,
  checkOutImage?: string,
  checkOutTime?: number
) =>
  api.post<{ success: boolean; data: Attendance }>('/attendance/check-out', {
    userId,
    latitude,
    longitude,
    ...(checkOutImage && { checkOutImage }),
    ...(checkOutTime && { checkOutTime }),
  });

export const getAttendance = (
  page: number = 1,
  pageSize: number = 10,
  userId?: string,
  startDate?: number,
  endDate?: number,
  status?: 'checkedIn' | 'checkedOut'
) =>
  api.get<{ success: boolean; data: Attendance[]; pagination: any }>('/attendance', {
    params: {
      page,
      pageSize,
      ...(userId && { userId }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(status && { status }),
    },
  });

export const getAttendanceById = (id: string) =>
  api.get<{ success: boolean; data: Attendance }>(`/attendance/${id}`);

export const getCurrentStatus = (
  userId: string,
  startOfDay?: number,
  endOfDay?: number
) =>
  api.get<{
    success: boolean;
    data: {
      status: string;
      checkInTime?: number;
      checkOutTime?: number;
      attendanceId?: string;
      checkInLocation?: { latitude: number; longitude: number; address?: string };
    };
  }>(`/attendance/status/${userId}`, {
    params: startOfDay != null && endOfDay != null ? { startOfDay, endOfDay } : {},
  });

// Notification APIs
export interface Notification {
  _id: string;
  metadata: {
    name: string;
    tenantId?: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    userId: string;
    title: string;
    body: string;
    type: 'booking_created' | 'booking_assigned' | 'booking_reassigned' | 'booking_in_transit' | 'booking_patient_pickedup' | 'booking_patient_dropped' | 'booking_completed' | 'booking_cancelled' | 'booking_updated' | 'ambulance_assign' | 'ambulance_unassign' | 'fuel_theft' | 'fuel_refill' | 'vehicle_halt' | 'system' | 'custom';
    priority: 'low' | 'medium' | 'high' | 'critical';
    data?: Record<string, any>;
    read: boolean;
    readAt?: number;
    sent: boolean;
    sentAt?: number;
    fcmToken?: string;
    relatedEntityId?: string;
    relatedEntityType?: string;
    bookingStatus?: 'pending' | 'assigned' | 'in_transit' | 'patient_picked' | 'patient_dropped' | 'completed' | 'cancelled';
  };
}

export const listNotifications = (
  page: number = 1,
  pageSize: number = 20,
  read?: boolean,
  type?: string,
  userId?: string,
  search?: string
) =>
  api.get<{ success: boolean; data: Notification[]; pagination: any }>('/notifications', {
    params: {
      page,
      pageSize,
      ...(read !== undefined && { read: read.toString() }),
      ...(type && { type }),
      ...(userId && { userId }),
      ...(search && { search }),
    },
  });

export const getNotificationById = (id: string) =>
  api.get<{ success: boolean; data: Notification }>(`/notifications/${id}`);

export const markNotificationAsRead = (id: string) =>
  api.patch<{ success: boolean; data: Notification }>(`/notifications/${id}/read`);

export const markAllNotificationsAsRead = (userId?: string) =>
  api.post<{ success: boolean; message: string }>('/notifications/mark-all-read', {
    ...(userId && { userId }),
  });

export const getUnreadNotificationCount = (userId?: string) =>
  api.get<{ success: boolean; data: { count: number } }>('/notifications/unread-count', {
    params: {
      ...(userId && { userId }),
    },
  });

export const downloadNotificationReportPDF = (startDate?: number, endDate?: number, type?: string, search?: string) => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (type) params.type = type;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/notifications/report/download-pdf', { params });
};

export const downloadNotificationReportExcel = (startDate?: number, endDate?: number, type?: string, search?: string) => {
  const params: any = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (type) params.type = type;
  if (search) params.search = search;
  return api.get<{ success: boolean; data: { url: string; fileName: string; filePath: string } }>('/notifications/report/download-excel', { params });
};

// Patient Vital Fields API
export interface VitalField {
  fieldLabel: string;
  fieldKey: string;
  fieldType: 'TEXT' | 'NUMBER';
  createdAt: number;
  updatedAt: number;
  isDeleted: boolean;
}

export interface PatientVitalFields {
  _id: string;
  metadata: {
    name: string;
    tenantId: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    fields: VitalField[];
  };
}

export const getPatientVitalFields = (tenantId?: string) =>
  api.get<{ success: boolean; data: PatientVitalFields }>('/patient-vital-fields', {
    params: {
      ...(tenantId && { tenantId }),
    },
  });

export const addVitalField = (fieldLabel: string, fieldType: 'TEXT' | 'NUMBER', tenantId?: string) =>
  api.post<{ success: boolean; data: PatientVitalFields; message: string }>('/patient-vital-fields/fields', {
    fieldLabel,
    fieldType,
    ...(tenantId && { tenantId }),
  });

export const updateVitalField = (fieldKey: string, fieldLabel?: string, fieldType?: 'TEXT' | 'NUMBER', tenantId?: string) =>
  api.put<{ success: boolean; data: PatientVitalFields; message: string }>('/patient-vital-fields/fields', {
    fieldKey,
    ...(fieldLabel !== undefined && { fieldLabel }),
    ...(fieldType !== undefined && { fieldType }),
    ...(tenantId && { tenantId }),
  });

export const deleteVitalField = (fieldKey: string, tenantId?: string) =>
  api.delete<{ success: boolean; data: PatientVitalFields; message: string }>('/patient-vital-fields/fields', {
    params: {
      fieldKey,
      ...(tenantId && { tenantId }),
    },
  });

export const updateAllVitalFields = (fields: VitalField[], tenantId?: string) =>
  api.put<{ success: boolean; data: PatientVitalFields; message: string }>('/patient-vital-fields', {
    fields,
    ...(tenantId && { tenantId }),
  });

// Area APIs
export interface Area {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    ambulances?: Array<{
      ambulanceId: string;
      name: string;
      licensePlate: string;
      vehicleNumber?: string;
      status?: string;
    }>;
    tenantId?: string;
  };
}

export const getAreas = (page: number = 1, pageSize: number = 100, search?: string) =>
  api.get<{ success: boolean; data: Area[]; pagination: any }>('/areas', {
    params: { page, pageSize, ...(search && { search }) },
  });

export const getArea = (id: string) =>
  api.get<{ success: boolean; data: Area }>(`/areas/${id}`);

export const createArea = (data: any) =>
  api.post<{ success: boolean; data: Area }>('/areas', data);

export const updateArea = (id: string, data: any) =>
  api.put<{ success: boolean; data: Area }>(`/areas/${id}`, data);

export const deleteArea = (id: string) =>
  api.delete<{ success: boolean; data: Area }>(`/areas/${id}`);

export default api;

